import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  setCollectionOverrides,
  type CollectionOverride,
} from '@/utils/artifact-collections';

/**
 * Loads collection overrides from Supabase into the in-memory cache used by
 * `getArtifactCollections`. Safe to mount on any page; the underlying state
 * is module-scoped so the latest fetch wins for the whole app.
 */
export const useCollectionOverrides = () => {
  const [overrides, setLocalOverrides] = useState<CollectionOverride[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await supabase
      .rpc('public_collection_overrides');
    if (fetchError) {
      setError(fetchError.message);
      setLoading(false);
      return;
    }
    const mapped: CollectionOverride[] = (data ?? []).map((row) => {
      // Reassignment overrides encode the source collection in `reason`
      // using the prefix "reassigned-from:<collection>".
      let replaces: string | undefined;
      if (row.action === 'reassign' && typeof row.reason === 'string') {
        const match = row.reason.match(/reassigned-from:([\w-]+)/);
        if (match) replaces = match[1];
      }
      return {
        artifactId: row.artifact_id,
        collection: row.collection,
        action: row.action as 'remove' | 'reassign' | 'flag',
        replaces,
        reviewStatus: (row.review_status as
          | 'pending'
          | 'confirmed'
          | 'removed'
          | 'reassigned'
          | undefined) ?? undefined,
      };
    });
    setCollectionOverrides(mapped);
    setLocalOverrides(mapped);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { overrides, loading, error, refresh };
};
